'use client'

import { HugeiconsIcon } from '@hugeicons/react'
import { ArrowRight01Icon, PlusSignIcon } from '@hugeicons-pro/core-stroke-rounded'
import { cn } from '@/lib/utils'
import { useGate } from './gate/GateProvider'

type Size = 'hero' | 'channel' | 'command'

const SIZE_CLASSES: Record<Size, string> = {
  hero: 'h-[288px] w-[200px] rounded-[18px] p-6 bg-transparent',
  channel: 'h-[172px] w-[220px] rounded-[14px] p-[18px] bg-transparent',
  command: 'h-[172px] w-[200px] rounded-[14px] p-[18px] bg-[#F4F2EC]',
}

export function PlaceholderCard({
  size,
  title,
  subtitle,
  variant = 'centered',
  icon = 'arrow',
}: {
  size: Size
  title: string
  subtitle: string
  variant?: 'centered' | 'command'
  icon?: 'arrow' | 'plus' | 'none'
}) {
  const { requireAuth } = useGate()
  const IconNode =
    icon === 'arrow'
      ? ArrowRight01Icon
      : icon === 'plus'
        ? PlusSignIcon
        : null

  if (variant === 'command') {
    return (
      <button
        type="button"
        onClick={() => requireAuth()}
        className={cn(
          'flex flex-none flex-col justify-between border-[0.5px] border-dashed border-[#C9C4B5] text-left transition-transform duration-200 ease-out [scroll-snap-align:start] hover:-translate-y-[3px] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/40',
          SIZE_CLASSES[size],
        )}
      >
        <div>
          <div className="mb-2 font-mono text-[11px] uppercase tracking-[0.08em] text-atrium-black/45">
            {title}
          </div>
          <div className="text-xl font-bold leading-[1.15] tracking-[-0.015em] text-atrium-black/55">
            {subtitle}
          </div>
        </div>
        <span className="inline-flex items-center gap-1.5 self-start rounded-lg border-[0.5px] border-atrium-black px-3.5 py-2 text-xs font-semibold text-atrium-black">
          Start in chat →
        </span>
      </button>
    )
  }

  return (
    <button
      type="button"
      onClick={() => requireAuth()}
      className={cn(
        'flex flex-none flex-col items-center justify-center border-[0.5px] border-dashed border-[#C9C4B5] text-center transition-transform duration-200 ease-out [scroll-snap-align:start] hover:-translate-y-[3px] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/40',
        SIZE_CLASSES[size],
      )}
    >
      {IconNode && (
        <span
          className={cn(
            'mb-2 grid place-items-center text-atrium-black/45',
            size === 'hero' ? 'size-9' : 'size-7',
          )}
          aria-hidden
        >
          <HugeiconsIcon
            icon={IconNode}
            className={size === 'hero' ? 'size-[34px]' : 'size-[26px]'}
          />
        </span>
      )}
      <div className="text-sm font-semibold text-atrium-black/55">{title}</div>
      <div className="mt-1 font-mono text-xs text-atrium-black/45">{subtitle}</div>
    </button>
  )
}
